// PayPalButton.js
import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { serverUrl } from "./ConfigHolder";
import { post_auth } from "./auth";

/**
 * Props:
 *  - amount: number
 *  - isOpen: boolean   // render only when dialog is actually open/visible
 *  - renderDelayMs?: number (default 300) // small delay to let dialog lay out
 */
export default function PayPalButton({ amount, 
                                        isOpen, 
                                        renderDelayMs = 300, 
                                        onPaymentResult }) {
  const containerRef = useRef(null);
  const renderedRef = useRef(false);       // prevent duplicate renders
  const buttonsRef = useRef(null);
  const timerRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    if (!clientId) {
      console.error("PayPal client ID is not defined");
      return;
    }
  if (!isOpen) return; // only proceed when dialog is open

    // Guard: if already rendered, do nothing
    if (renderedRef.current) return;

  // Delay a bit so the dialog fully lays out
    // schedule a single render if one isn't pending already
    if (renderedRef.current || timerRef.current) {
      // already rendered or scheduled
      return;
    }
    timerRef.current = setTimeout(async () => {
      // Temporarily silence console output while PayPal SDK initialises.
      // The SDK sometimes logs an informational object (e.g. loadedInFrame) which
      // is noisy in dev console. We still preserve console.error for real errors.
      let _origWarn, _origLog, _origInfo;
      try {
        _origWarn = console.warn;
        _origLog = console.log;
        _origInfo = console.info;
        console.warn = () => {};
        console.log = () => {};
        console.info = () => {};
      } catch (e) {
        // ignore if we can't override console
      }
      try {
  // Attempt to load PayPal SDK
        const paypal = await loadPayPalSdkOnce({
          clientId,
          currency: "USD",
          intent: "capture",
        });
  // SDK loaded
        if (!paypal || !containerRef.current || renderedRef.current) return;

        buttonsRef.current = paypal.Buttons({
          // Styling to better match Material UI: use 'pill' shape and a neutral color.
          // Note: PayPal supports limited style options; we choose the combination
          // that most closely resembles a rounded MUI button.
          style: {
            layout: "vertical",
            color: "gold", // 'gold' has better contrast for PayPal mark
            shape: "pill",
            label: "pay",
            height: 44,
            tagline: false,
          },

          createOrder: async () => {
            try {
              const res = await post_auth(`${serverUrl}/create_paypal_order`, {
                payment_total: amount,
              });
              const data = res?.data || {};
              if (data.orderId) return data.orderId;
              // fallback: some endpoints return an approval_url containing the token/order id
              if (data.approval_url) {
                const m = /[?&]token=([^&]+)/.exec(data.approval_url);
                if (m) return m[1];
              }
              console.error('create_paypal_order did not return orderId', data);
              throw new Error('No orderId returned from server');
            } catch (err) {
              console.error('createOrder error', err);
              throw err; // propagate to PayPal SDK so it can handle it
            }
          },

          onApprove: async (data) => {
            try {
              const res = await post_auth(`${serverUrl}/capture_paypal_order`, {
                orderId: data.orderID,
              });
              const capture = res.data;
              if (capture.status === "COMPLETED") {
                // include server-computed renewal date if present so frontend can use it directly
                const renewal_date = capture.renewal_date || capture.renewalDate || null;
                if (typeof onPaymentResult === 'function') onPaymentResult({ status: 'success', data: capture, renewal_date });
              } else {
                if (typeof onPaymentResult === 'function') onPaymentResult({ status: 'error', message: 'Payment failed or pending', data: capture });
              }
            } catch (err) {
              console.error('capture error', err);
              if (typeof onPaymentResult === 'function') onPaymentResult({ status: 'error', message: err?.message || 'Capture failed' });
            }
          },

          onError: (err) => {
            console.error("PayPal Checkout error", err);
            if (typeof onPaymentResult === 'function') onPaymentResult({ status: 'error', message: 'PayPal Checkout error', data: err });
          },
        });

        await buttonsRef.current.render(containerRef.current);
        renderedRef.current = true;
        setLoading(false);
        // Buttons rendered
      } catch (e) {
          console.error("PayPal init/render error", e);
      } finally {
        // restore console methods we replaced above
        try {
          if (_origWarn) console.warn = _origWarn;
          if (_origLog) console.log = _origLog;
          if (_origInfo) console.info = _origInfo;
        } catch (err) {
          // ignore restore errors
        }
  // ensure spinner hidden on error/cleanup
        setLoading(false);
      }
    }, renderDelayMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Do NOT forcibly clear container or destroy iframe here; that can trigger SDK errors.
    };
  }, [isOpen, amount, renderDelayMs]);

  // Wrap the PayPal iframe in an MUI Box so we can control padding/width
  return (
    <Box sx={{
      width: '100%',
      maxWidth: { xs: '100vw', sm: 560 },
      mx: 'auto',
      // give the PayPal iframe more vertical room; responsive sizes:
      minHeight: { xs: 520, sm: 360 },   // mobile: larger so credit-card UI fits
      overflowX: 'visible',              // allow iframe to overflow if it needs to render fields
      overflowY: 'visible',
      boxSizing: 'border-box',
      px: 0,
      position: 'relative', // so overlay and spinner can be absolutely positioned
    }}>
      <div ref={containerRef} style={{ width: '100%', minWidth: '100%', height: '100%' }} />
      {/* Overlay that captures pointer events while PayPal SDK/buttons are loading */}
      {loading && (
        <>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background: 'transparent',
              zIndex: 10,
              // capture pointer events so underlying paypal buttons are not clickable
              pointerEvents: 'all',
            }}
          />
          <CircularProgress
            size={24}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: -12,
              marginLeft: -12,
              zIndex: 11, // above the overlay
            }}
          />
        </>
      )}
    </Box>
  );
}

/** Single, parameter-consistent SDK loader cached on window. */
function loadPayPalSdkOnce({ clientId, currency = "USD", intent = "capture" }) {
  // Disable Pay Later funding source so that Pay Later button does not appear.
  // Note: some tokens like 'fastlane' are not valid for disable-funding and cause the SDK to return 400.
  const src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}&intent=${intent}&disable-funding=paylater`;

  if (window.paypal && window.__paypalSdkSrc === src) {
    return Promise.resolve(window.paypal);
  }
  if (window.__paypalLoadedPromise && window.__paypalSdkSrc === src) {
    return window.__paypalLoadedPromise;
  }

  // If a different SDK was loaded before, remove it and load the correct one.
  let script = document.querySelector("#paypal-sdk");
  if (script && script.src !== src) {
    script.remove();
    script = null;
  }

  if (!script) {
    script = document.createElement("script");
    script.id = "paypal-sdk";
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  }

  window.__paypalSdkSrc = src;
  window.__paypalLoadedPromise = new Promise((resolve, reject) => {
    script.addEventListener("load", () => resolve(window.paypal));
    script.addEventListener("error", () => reject(new Error("PayPal SDK could not be loaded")));
  });

  return window.__paypalLoadedPromise;
}
// PayPalButton.js